import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, Grid, IconButton, Typography, useMediaQuery } from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { useToast } from 'context/ToastContext';
import CustomInputField from 'ui-components/CustomInputField';
import { Secondary } from 'ui-components/Buttons';
import { FetchAdminConfig, FetchAdminConfigRevisions, RestoreAdminConfigRevision, UpdateAdminConfig } from 'api/admin_configs';
import Section from './plans/Section';
import SaveBar from './plans/SaveBar';
import HistoryPanel, { HistoryButton } from './plans/HistoryPanel';

const TYPE = 'utm_campaign_list';
// Same rule as the backend: names become Mongo keys, so no dots or `$`.
const NAME_RULE = /^[A-Za-z0-9_-]{1,60}$/;

const toRows = (list = {}) => Object.entries(list).map(([name, templateId]) => ({ name, templateId: String(templateId) }));
// A row left completely empty is just a placeholder for the next campaign, so it is neither validated nor saved.
const isBlankRow = (row) => !row.name.trim() && !String(row.templateId ?? '').trim();
const toList = (rows) => Object.fromEntries(rows.filter((r) => !isBlankRow(r)).map((r) => [r.name.trim(), Number(r.templateId)]));

const describeRevision = (paths = []) => {
    const names = paths.filter((p) => p.startsWith('list.')).map((p) => p.slice(5));
    return names.length ? `Changed: ${names.join(', ')}` : paths.length ? 'Campaign list changed' : 'No campaigns changed';
};

/**
 * UTM campaigns: which Brevo email template a `?utm_campaign=<name>` link sends.
 * Opening a link with a listed campaign emails that template once per user.
 */
const UtmCampaigns = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { addToast } = useToast();
    const c = theme.palette.customColors;

    const { data: config, isLoading, refetch } = FetchAdminConfig(TYPE);
    const { mutateAsync: saveConfig, isLoading: isSaving } = UpdateAdminConfig(TYPE);
    const { mutateAsync: restoreRevision, isLoading: isRestoring } = RestoreAdminConfigRevision(TYPE);
    const [historyOpen, setHistoryOpen] = React.useState(false);
    const { data: revisions, isLoading: loadingRevisions, refetch: refetchRevisions } = FetchAdminConfigRevisions(TYPE, { enabled: historyOpen });

    const [rows, setRows] = React.useState(null);
    const [baseline, setBaseline] = React.useState(null);
    const [updatedAt, setUpdatedAt] = React.useState(null);
    const [serverProblems, setServerProblems] = React.useState([]);

    const load = React.useCallback((saved) => {
        const next = toRows(saved?.data?.list);
        setRows(next);
        setBaseline(JSON.stringify(next));
        setUpdatedAt(saved?.updated_at || null);
        setServerProblems([]);
    }, []);

    // Loaded once; a background refetch must not overwrite edits in progress.
    React.useEffect(() => { if (config && !rows) load(config); }, [config, rows, load]);

    const dirty = Boolean(rows) && JSON.stringify(rows) !== baseline;

    const rowProblem = (row, index) => {
        if (isBlankRow(row)) return null;
        const name = row.name.trim();
        if (!NAME_RULE.test(name)) return 'Use letters, numbers, - or _ (no spaces, max 60).';
        if (rows.findIndex((r) => r.name.trim() === name) !== index) return 'This campaign is already listed.';
        if (!/^[1-9][0-9]*$/.test(row.templateId)) return 'Enter the Brevo template ID (a number).';
        return null;
    };
    const problems = [
        ...(rows || []).map((row, index) => {
            const message = rowProblem(row, index);
            return message ? { path: `row${index}`, message: `${row.name.trim() || `Row ${index + 1}`}: ${message}` } : null;
        }).filter(Boolean),
        ...serverProblems,
    ];

    const update = (index, patch) => {
        setServerProblems([]);
        setRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const handleSave = async () => {
        if (problems.length) return;
        try {
            const response = await saveConfig({ data: { list: toList(rows) }, updated_at: updatedAt });
            load(response?.data?.data);
            addToast('Saved. Campaign links use it right away.', 'success');
            refetch();
            refetchRevisions();
        } catch (error) {
            const body = error?.response?.data;
            const details = body?.message?.errors || body?.errors;
            if (Array.isArray(details) && details.length) setServerProblems(details.map((message) => ({ path: 'server', message })));
            else addToast(body?.message || 'Error: Something went wrong.', 'error');
        }
    };

    if (isLoading || !rows) {
        return (
            <Grid item xs={12} paddingInline={isSmallScreen ? '1rem' : '2rem'}>
                <Box className='skeleton' minHeight={"40dvh"} sx={{ borderRadius: '8px', border: `1px solid ${c.b50}` }} />
            </Grid>
        );
    }

    return (
        <Grid container paddingInline={isSmallScreen ? '1rem' : '2rem'} display="grid" gap="1rem">
            <Section
                title="UTM campaigns"
                description="When a signed-in investor or startup opens a link with ?utm_campaign=<name>, they get this Brevo email template once. Removing a campaign stops its links from sending."
                action={<HistoryButton onClick={() => setHistoryOpen(true)} />}
            >
                <Box sx={{ display: 'grid', gap: '0.75rem' }}>
                    {rows.length === 0 && (
                        <Typography sx={{ fontSize: '0.875rem', color: c.g200 }}>No campaigns yet.</Typography>
                    )}
                    {rows.length > 0 && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 10rem 2.5rem', gap: '0.75rem' }}>
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: c.g500 }}>Campaign (utm_campaign)</Typography>
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: c.g500 }}>Brevo template ID</Typography>
                            <span />
                        </Box>
                    )}
                    {rows.map((row, index) => {
                        const problem = rowProblem(row, index);
                        return (
                            <Box key={index} sx={{ display: 'grid', gridTemplateColumns: '1fr 10rem 2.5rem', gap: '0.75rem', alignItems: 'start' }}>
                                <CustomInputField
                                    value={row.name}
                                    placeholder="e.g. angel_db"
                                    inputProps={{ 'aria-label': `Campaign ${index + 1}`, maxLength: 60 }}
                                    onChange={(event) => update(index, { name: event.target.value.replace(/\s/g, '') })}
                                    hasError={Boolean(problem) && !NAME_RULE.test(row.name.trim())}
                                    sx={{ marginBottom: 0 }}
                                />
                                <CustomInputField
                                    value={row.templateId}
                                    placeholder="e.g. 27"
                                    inputProps={{ 'aria-label': `Template ID ${index + 1}`, inputMode: 'numeric', maxLength: 9 }}
                                    onChange={(event) => update(index, { templateId: event.target.value.replace(/[^0-9]/g, '') })}
                                    hasError={Boolean(problem) && !/^[1-9][0-9]*$/.test(row.templateId)}
                                    sx={{ marginBottom: 0 }}
                                />
                                <IconButton aria-label={`Remove ${row.name || 'campaign'}`} onClick={() => setRows(rows.filter((_, i) => i !== index))} sx={{ marginTop: '0.25rem' }}>
                                    <DeleteOutline sx={{ color: c.r400 }} />
                                </IconButton>
                            </Box>
                        );
                    })}
                    <Box>
                        <Secondary startIcon={<Add />} sx={{ padding: '0.375rem 1rem' }} onClick={() => setRows([...rows, { name: '', templateId: '' }])}>
                            Add campaign
                        </Secondary>
                    </Box>
                </Box>
            </Section>

            <SaveBar
                dirty={dirty}
                saving={isSaving}
                summary={`${rows.length} campaign${rows.length === 1 ? '' : 's'}`}
                problems={dirty ? problems : serverProblems}
                target="campaign links use it right away"
                saveLabel="Save"
                onSave={handleSave}
                onDiscard={() => { setRows(JSON.parse(baseline)); setServerProblems([]); }}
            />

            <HistoryPanel
                open={historyOpen}
                handleClose={() => setHistoryOpen(false)}
                revisions={revisions || []}
                loading={loadingRevisions}
                restoring={isRestoring}
                describeRevision={describeRevision}
                restoreWhat="The campaign list"
                onRestore={async (revision) => {
                    try {
                        const response = await restoreRevision({ _id: revision._id });
                        load(response?.data?.data);
                        setHistoryOpen(false);
                        addToast('Restored.', 'success');
                        refetch();
                        refetchRevisions();
                    } catch (error) {
                        addToast(error?.response?.data?.message || 'Could not restore that version.', 'error');
                    }
                }}
            />
        </Grid>
    );
};

export default UtmCampaigns;
